import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "../context/Provider";
import { toast } from 'react-toastify';
import { Card, CardContent } from "./ui/card";
import { cn } from "../lib/utils";

interface DashboardCardProps {
    url: string;
    title: string;
    count: string | number;
    description: string;
    icon?: string;
    loading?: boolean;
    badge?: string;
    badgeType?: 'success' | 'info' | 'warning' | 'neutral';
    trend?: string;
    extraNote?: string;
}

const Skeleton = ({ className }: { className?: string }) => (
    <div className={cn("animate-pulse rounded-md bg-slate-200/80", className)} />
);

const DashboardCard: React.FC<DashboardCardProps> = (props) => {
    const { authState } = useContext(GlobalContext);
    const isLoading = !!props.loading;

    return (
        <Link
            to={authState?.is_admin_verified === 'pending' || authState?.is_admin_verified === 'reject' ? '#' : props.url}
            className="text-decoration-none block h-full group"
            onClick={(e) => {
                if (isLoading) return;
                if (authState?.is_admin_verified === 'pending') {
                    e.preventDefault();
                    toast.error("Your account is pending for admin verification.");
                }
                if (authState?.is_admin_verified === 'reject') {
                    e.preventDefault();
                    toast.error("Your account is rejected for admin verification.");
                }
            }}
        >
            <Card className="h-full bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md hover:border-sky-300 transition-all duration-200">
                <CardContent className="p-4 flex flex-col justify-between h-full space-y-3">
                    {/* Top Row: Title & Icon */}
                    <div className="flex justify-between items-start gap-2">
                        <span className="text-sm sm:text-base font-bold text-slate-700 uppercase tracking-wider">
                            {props.title}
                        </span>
                        {isLoading ? (
                            <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
                        ) : (
                            <div className="h-10 w-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center text-lg group-hover:scale-105 group-hover:bg-[#1AA7DE] group-hover:text-white transition-all duration-200 shrink-0">
                                <i className={`fa ${props.icon || 'fa-chart-line'}`}></i>
                            </div>
                        )}
                    </div>

                    {/* Middle Row: Value & Description */}
                    <div>
                        {isLoading ? (
                            <>
                                <Skeleton className="h-8 w-24 mb-2.5" />
                                <Skeleton className="h-4 w-40" />
                            </>
                        ) : (
                            <>
                                <h2 className="text-3xl font-black text-slate-900 tracking-tight m-0 mb-1.5 leading-none">
                                    {props.count}
                                </h2>
                                <p className="text-sm sm:text-base text-slate-500 font-medium m-0 leading-relaxed">
                                    {props.description}
                                </p>
                            </>
                        )}
                    </div>

                    {/* Bottom Row: Badge / Trend */}
                    {isLoading ? (
                        <div className="pt-2.5 border-t border-slate-100">
                            <Skeleton className="h-6 w-24 rounded-full" />
                        </div>
                    ) : (
                        (props.badge || props.trend || props.extraNote) && (
                            <div className="pt-2.5 flex items-center justify-between text-xs sm:text-sm border-t border-slate-100 gap-2">
                                {props.badge && (
                                    <span className={cn(
                                        "inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-xs whitespace-nowrap shrink-0",
                                        props.badgeType === 'success' && "bg-emerald-50 text-emerald-700 border border-emerald-200/60",
                                        props.badgeType === 'info' && "bg-sky-50 text-sky-700 border border-sky-200/60",
                                        props.badgeType === 'warning' && "bg-amber-50 text-amber-700 border border-amber-200/60",
                                        (!props.badgeType || props.badgeType === 'neutral') && "bg-slate-100 text-slate-600"
                                    )}>
                                        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                        {props.badge}
                                    </span>
                                )}
                                {props.trend && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full font-bold text-xs bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                                        ↗ {props.trend}
                                    </span>
                                )}
                                {props.extraNote && (
                                    <span className="text-slate-400 text-xs font-medium">
                                        {props.extraNote}
                                    </span>
                                )}
                            </div>
                        )
                    )}
                </CardContent>
            </Card>
        </Link>
    );
};

export default DashboardCard;